import React, { useState, useContext, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';

const RegisterPage = () => {
    const [formData, setFormData] = useState({ name: '', email: '', password: '', confirmPassword: '' });
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [loading, setLoading] = useState(false);
    const { register, loginWithGoogle, user } = useContext(AuthContext);
    const navigate = useNavigate();
    const googleButtonRef = useRef(null);
    const isGoogleLogin = useRef(false); // Google login flag

    useEffect(() => {
        // Sadece sayfa yüklendiginde user varsa yönlendir (Google login degilse)
        if (user && !isGoogleLogin.current) {
            navigate('/');
        }
    }, [user, navigate]);

    const handleGoogleResponse = useCallback(async (response) => {
        isGoogleLogin.current = true; // Google login basliyor
        const loadingToast = toast.loading('Signing up with Google...');
        const result = await loginWithGoogle(response.credential);
        
        if (result.success) {
            toast.success('Account created successfully!', { id: loadingToast });
            
            // Profil tamamlanmamışsa onboarding'e yönlendir
            if (!result.user.profileCompleted) {
                navigate('/onboarding');
            } else {
                navigate('/');
            }
        } else {
            toast.error(result.message || 'Google sign-up failed', { id: loadingToast });
        }
        isGoogleLogin.current = false; // İşlem bitti
    }, [loginWithGoogle, navigate]);

    useEffect(() => {
        // Google Sign-In butonu oluştur
        if (window.google && googleButtonRef.current) {
            window.google.accounts.id.initialize({
                client_id: '927028296542-otv90gcva097t81esl70p7chqhhhoj5q.apps.googleusercontent.com',
                callback: handleGoogleResponse,
            });

            window.google.accounts.id.renderButton(
                googleButtonRef.current,
                { 
                    theme: 'outline', 
                    size: 'large',
                    width: googleButtonRef.current.offsetWidth,
                    text: 'signup_with',
                    shape: 'rectangular',
                }
            );
        }
    }, [handleGoogleResponse]);

    const onChange = e => setFormData({ ...formData, [e.target.name]: e.target.value });

    const onSubmit = async e => {
        e.preventDefault();
        if (loading) return; // Zaten loading ise çık
    
        if (!acceptedTerms) {
            toast.error('You must accept the Terms of Service and Privacy Policy to create an account.');
            setLoading(false);
            return;
        }

        if (formData.password.length < 8) {
            toast.error('Password must be at least 8 characters.');
            return;
        }
        if (formData.password !== formData.confirmPassword) {
            toast.error('Passwords do not match.');
            return;
        }
        
        setLoading(true);
        const loadingToast = toast.loading('Creating account...');
        const result = await register(formData.name, formData.email, formData.password);
        
        if (result.success) {
            toast.success('Your account has been successfully created!', { id: loadingToast });
            navigate('/');
        } else {
            toast.error(result.message, { id: loadingToast });
        }
        setLoading(false);
    };

    return (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
            className="max-w-md mx-auto card p-8 mt-8">
            <h2 className="text-3xl font-bold text-center mb-6">Create Account</h2>
            <form onSubmit={onSubmit} className="space-y-4">
                <input type="text" name="name" value={formData.name} onChange={onChange} required placeholder="Full Name" className="input-field"/>
                <input type="email" name="email" value={formData.email} onChange={onChange} required placeholder="Email Address" className="input-field"/>
                <input type="password" name="password" value={formData.password} onChange={onChange} required placeholder="Password" className="input-field"/>
                <input type="password" name="confirmPassword" value={formData.confirmPassword} onChange={onChange} required placeholder="Confirm Password" className="input-field"/>

                <div className="flex flex-col gap-2 mt-2">
                    <label className="inline-flex items-start gap-2 text-sm text-light-text/80 dark:text-dark-text/80">
                        <input type="checkbox" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} className="mt-1" />
                        <span>
                            By creating an account you agree to our{' '}
                            <Link to="/terms" className="underline">Terms of Service</Link>{' '}and{' '}
                            <Link to="/privacy" className="underline">Privacy Policy</Link>.
                        </span>
                    </label>
                </div>

                <button type="submit" disabled={loading || !acceptedTerms} className="btn-primary w-full !text-lg disabled:opacity-50 disabled:cursor-not-allowed">
                    {loading ? 'Creating Account...' : 'Sign Up'}
                </button>
            </form>
            
            {/* Divider */}
            <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-light-border dark:border-dark-border"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-light-card dark:bg-dark-card text-light-text/60 dark:text-dark-text/60">
                        Or continue with
                    </span>
                </div>
            </div>

            {/* Google Sign-In Button */}
            <div ref={googleButtonRef} className="w-full"></div>

            <p className="text-center mt-6">
                Already have an account?{' '}
                <Link to="/login" className="font-semibold text-light-primary dark:text-dark-primary hover:underline">
                    Log In
                </Link>
            </p>
        </motion.div>
    );
};

export default RegisterPage;
